import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Users, ChevronDown, ChevronRight, Phone, Mail, CheckCircle2, StickyNote } from "lucide-react";
import api from "@/lib/api";

export default function EventRoster() {
  const { data, isLoading } = useQuery({
    queryKey: ["enhancer-event-roster"],
    queryFn: () => api.get("/enhancer/me/event-roster").then((r) => r.data),
  });
  const events = data?.events || [];

  if (isLoading) return <div className="py-12 text-center text-[#4A5D6B]"><Loader2 className="animate-spin inline" /></div>;

  return (
    <div data-testid="enhancer-event-roster">
      <div className="mb-4">
        <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Users size={22} className="text-[#144F75]" /> Event roster</h2>
        <p className="text-sm text-[#4A5D6B]">Everyone attending the events you facilitate, with their contact info, notes & profile fields.</p>
      </div>
      {events.length === 0 ? (
        <p className="text-sm text-[#4A5D6B] eyw-card p-6">No events assigned to you yet. Events list you as facilitator will appear here.</p>
      ) : (
        <div className="space-y-4">{events.map((ev) => <EventBlock key={ev.id} ev={ev} />)}</div>
      )}
    </div>
  );
}

function EventBlock({ ev }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="eyw-card overflow-hidden" data-testid={`roster-event-${ev.id}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-5 py-4 text-left">
        <div>
          <h3 className="font-serif text-lg text-[#1A242B]">{ev.title}</h3>
          <p className="text-xs text-[#8FA89B]">{[ev.date, ev.start_time].filter(Boolean).join(" · ")} · {ev.location || "—"}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-[#144F75]">{ev.checked_in_count}/{ev.attendee_count} in</span>
          {open ? <ChevronDown size={18} className="text-[#4A5D6B]" /> : <ChevronRight size={18} className="text-[#4A5D6B]" />}
        </div>
      </button>
      {open && (
        <div className="border-t border-[#E5E0D8] divide-y divide-[#E5E0D8]">
          {ev.attendees.length === 0 && <p className="px-5 py-6 text-sm text-[#4A5D6B]">No attendees yet.</p>}
          {ev.attendees.map((a) => <Attendee key={a.id} a={a} />)}
        </div>
      )}
    </div>
  );
}

function Attendee({ a }) {
  const [expand, setExpand] = useState(false);
  const cf = Object.entries(a.custom_fields || {}).filter(([, v]) => v);
  const hasExtra = (a.notes && a.notes.length > 0) || cf.length > 0;
  return (
    <div className="px-5 py-3" data-testid={`roster-attendee-${a.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-[#1A242B] flex items-center gap-2">
            {a.name}
            {a.checked_in && <CheckCircle2 size={14} className="text-[#5C8A72]" />}
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-[#4A5D6B] mt-0.5">
            {a.email && <span className="inline-flex items-center gap-1"><Mail size={11} />{a.email}</span>}
            {a.phone && <span className="inline-flex items-center gap-1"><Phone size={11} />{a.phone}</span>}
            {a.tier && <span>Tier: {a.tier}</span>}
            {a.meal_option && <span className="text-[#5C8A72]">Meal: {a.meal_option}</span>}
            {a.lodging_option && <span className="text-[#B5794E]">Stay: {a.lodging_option}</span>}
            <span className={a.payment_status === "paid" ? "text-[#144F75]" : "text-[#B5794E]"}>{a.payment_status}</span>
          </div>
        </div>
        {hasExtra && (
          <button onClick={() => setExpand((v) => !v)} data-testid={`roster-attendee-toggle-${a.id}`} className="text-xs text-[#144F75] hover:underline shrink-0">
            {expand ? "Hide" : "Details"}
          </button>
        )}
      </div>
      {expand && hasExtra && (
        <div className="mt-2 pl-1 space-y-2">
          {cf.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {cf.map(([k, v]) => <span key={k} className="text-[11px] rounded-full bg-[#144F75]/10 text-[#144F75] px-2 py-0.5">{k.replace(/_/g, " ")}: {v}</span>)}
            </div>
          )}
          {(a.notes || []).map((n, i) => (
            <p key={i} className="text-xs text-[#4A5D6B] flex gap-1.5"><StickyNote size={12} className="text-[#8FA89B] shrink-0 mt-0.5" /><span><b className="capitalize">{n.category}:</b> {n.body}</span></p>
          ))}
        </div>
      )}
    </div>
  );
}
